import { isValidCalendarDate } from './dates';
import type { InvoiceDataQualityReport, InvoiceDataQualityRow, InvoiceStatus } from './types';

export const QUALITY_LABELS = {
  missing_dates: 'Missing issue and paid dates', missing_issue_date: 'Missing issue date',
  missing_due_date: 'Missing due date', zero_amount: 'Amount is $0',
  paid_before_issue: 'Paid before issue date', duplicate_numbers: 'Duplicate invoice number', missing_client: 'Missing client',
} as const;
export type QualityCategory = keyof typeof QUALITY_LABELS;
export interface QualityInvoice extends InvoiceDataQualityRow {
  client_id: string; project_id: string | null; project_name: string | null;
  gst_cents: number; due_date: string | null; updated_at: number;
}
export interface QualityIssue { key: string; category: QualityCategory; invoice: QualityInvoice }
export interface QualityClient {
  id: string; name: string; contact_email: string | null; emails: string[];
  secondary_contact_email?: string | null; contact_snapshot?: string;
  projects: number; invoices: number; dashboard_slug: string | null; updated_at: number;
}
export interface DuplicateClients { key: string; reason: 'name' | 'email'; clients: [QualityClient, QualityClient] }
export interface QualityProject { id: string; name: string; project_number: number | null; phase: string; value_cents: number | null; client_id: string; updated_at: number }
export type QualityReport = InvoiceDataQualityReport & {
  issues: QualityIssue[]; duplicate_clients: DuplicateClients[]; projects_without_client: QualityProject[]; total_issues: number;
};
export function normalizedClientName(value: string): string { return value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase(); }
export function duplicateClientPairs(clients: QualityClient[]): DuplicateClients[] {
  const pairs: DuplicateClients[] = [];
  for (let a = 0; a < clients.length; a++) for (let b = a + 1; b < clients.length; b++) {
    const left = clients[a]!, right = clients[b]!;
    const name = normalizedClientName(left.name);
    const namesMatch = name !== '' && name === normalizedClientName(right.name);
    const emailMatches = left.emails.some(email => email.trim() !== '' && right.emails.some(other => other.trim().toLowerCase() === email.trim().toLowerCase()));
    if (namesMatch || emailMatches) pairs.push({ key: [left.id, right.id].sort().join(':'), reason: namesMatch ? 'name' : 'email', clients: [left, right] });
  }
  return pairs;
}
export function invoiceQualityIssues(rows: QualityInvoice[]): QualityIssue[] {
  const live = rows.filter(row => row.status !== 'void');
  const counts = new Map<string, number>();
  for (const row of live) counts.set(row.number, (counts.get(row.number) ?? 0) + 1);
  return live.flatMap(invoice => {
    const categories: QualityCategory[] = [];
    if (invoice.issue_date === null && invoice.paid_date === null) categories.push('missing_dates');
    else if (invoice.issue_date === null) categories.push('missing_issue_date');
    if (['sent', 'overdue'].includes(invoice.status) && invoice.due_date === null) categories.push('missing_due_date');
    if (invoice.amount === 0) categories.push('zero_amount');
    if (invoice.issue_date && invoice.paid_date && invoice.paid_date < invoice.issue_date) categories.push('paid_before_issue');
    if (counts.get(invoice.number)! > 1) categories.push('duplicate_numbers');
    if (!invoice.client?.trim()) categories.push('missing_client');
    return categories.map(category => ({ key: `${invoice.id}:${category}`, category, invoice }));
  });
}
export function sourceNote(body: Record<string, unknown>): string | null {
  return typeof body.source === 'string' && body.source.trim().length > 0 && body.source.trim().length <= 2000 ? body.source.trim() : null;
}
export function dateCorrection(category: QualityCategory, status: InvoiceStatus, body: Record<string, unknown>): Record<string, string> | null {
  const allowed = category === 'missing_due_date' ? ['due_date'] : category === 'missing_issue_date' || (category === 'missing_dates' && status !== 'paid') ? ['issue_date'] : ['issue_date', 'paid_date'];
  const fields: Record<string, string> = {};
  for (const key of allowed) {
    if (typeof body[key] !== 'string' || !isValidCalendarDate(body[key])) return null;
    fields[key] = body[key];
  }
  if (fields.paid_date && fields.issue_date && fields.paid_date < fields.issue_date) return null;
  return fields;
}
